import {
  type NotifiedPartyEnum,
  SafeguardingEnquiryOutcomeEnum
} from '@lmc/validations'
import { Form as AntdForm, DatePicker, Input, Select } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import StepCheckbox from '../../StepCheckbox'
import StepField from '../../StepField'
import { StepReveal, StepRevealGroup } from '../../StepReveal'
import { DATE_FORMAT } from '../../stepLayout'
import { hasEnquiryDetail } from '../../stepValues'

/** The enquiry outcomes in design order, with their label keys. */
export const ENQUIRY_OUTCOMES: ReadonlyArray<{
  labelKey: string
  outcome: SafeguardingEnquiryOutcomeEnum
}> = [
  {
    labelKey:
      incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_OUTCOME_ALL,
    outcome: SafeguardingEnquiryOutcomeEnum.all
  },
  {
    labelKey:
      incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_OUTCOME_SOME,
    outcome: SafeguardingEnquiryOutcomeEnum.some
  },
  {
    labelKey:
      incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_OUTCOME_NONE,
    outcome: SafeguardingEnquiryOutcomeEnum.none
  }
]

/** The enquiry fields, emptied. */
export const NO_ENQUIRY = {
  enquiryClosedAt: null,
  enquiryLocalAuthority: undefined,
  enquiryOutcome: undefined,
  enquiryOutcomeAndRecommendation: undefined,
  enquiryType: undefined
}

/** Why a date closed can't be taken, as a locale key; null when it can. */
const closedAtProblem = (
  closedAt: Dayjs,
  submittedAt: Dayjs | null | undefined
) => {
  if (closedAt.isAfter(dayjs(), 'day')) {
    return incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_CLOSED_AT_FUTURE
  }
  return submittedAt && closedAt.isBefore(submittedAt, 'day')
    ? incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_CLOSED_AT_BEFORE_SUBMITTED
    : null
}

type SafeguardingEnquiryFieldsProps = {
  party: NotifiedPartyEnum
  testId: string
}

/**
 * "Progressed to an enquiry", and once ticked, the enquiry's details. None is
 * required; unticking asks before it drops any that are recorded.
 */
const SafeguardingEnquiryFields = ({
  party,
  testId
}: SafeguardingEnquiryFieldsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const form = AntdForm.useFormInstance()
  const isEnquiry = AntdForm.useWatch([party, 'progressedToEnquiry'], form)
  const submittedAt: Dayjs | null | undefined = AntdForm.useWatch(
    [party, 'notifiedAt'],
    form
  )
  const enquiryTestId = `${testId}-progressed-to-enquiry`

  return (
    <StepRevealGroup>
      <AntdForm.Item
        name={[party, 'progressedToEnquiry']}
        valuePropName="checked"
        noStyle
      >
        <StepCheckbox
          confirmUntick={{
            body: t(
              incidentReportsConstants.CASE_V2_NOTIFICATIONS_DISCARD_BODY
            ),
            isNeeded: (instance) =>
              hasEnquiryDetail(instance.getFieldValue(party)),
            onConfirm: (instance) =>
              instance.setFieldValue(party, {
                ...instance.getFieldValue(party),
                ...NO_ENQUIRY
              }),
            title: t(
              incidentReportsConstants.CASE_V2_NOTIFICATIONS_DISCARD_TITLE
            )
          }}
          testId={enquiryTestId}
        >
          {t(
            incidentReportsConstants.CASE_V2_NOTIFICATIONS_PROGRESSED_TO_ENQUIRY
          )}
        </StepCheckbox>
      </AntdForm.Item>
      {isEnquiry && (
        <StepReveal data-testid={`${enquiryTestId}-details`}>
          <StepField
            name={[party, 'enquiryType']}
            label={t(
              incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_TYPE
            )}
            hint={t(
              incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_TYPE_HINT
            )}
          >
            <Input data-testid={`${enquiryTestId}-type`} />
          </StepField>
          <StepField
            name={[party, 'enquiryLocalAuthority']}
            label={t(
              incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_LOCAL_AUTHORITY
            )}
          >
            <Input data-testid={`${enquiryTestId}-local-authority`} />
          </StepField>
          <StepField
            name={[party, 'enquiryOutcome']}
            label={t(
              incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_OUTCOME
            )}
          >
            <Select
              data-testid={`${enquiryTestId}-outcome`}
              allowClear
              options={ENQUIRY_OUTCOMES.map(({ labelKey, outcome }) => ({
                label: t(labelKey),
                value: outcome
              }))}
              placeholder={t(
                incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER
              )}
            />
          </StepField>
          <StepField
            name={[party, 'enquiryOutcomeAndRecommendation']}
            label={t(
              incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_OUTCOME_RECOMMENDATION
            )}
          >
            <Input.TextArea
              data-testid={`${enquiryTestId}-outcome-and-recommendation`}
              autoSize={{ minRows: 3 }}
            />
          </StepField>
          <StepField
            name={[party, 'enquiryClosedAt']}
            label={t(
              incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_CLOSED_AT
            )}
            dependencies={[[party, 'notifiedAt']]}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, closedAt: Dayjs | null | undefined) => {
                  const problem =
                    closedAt &&
                    closedAtProblem(
                      closedAt,
                      getFieldValue([party, 'notifiedAt'])
                    )
                  return problem
                    ? Promise.reject(new Error(t(problem)))
                    : Promise.resolve()
                }
              })
            ]}
          >
            <DatePicker
              data-testid={`${enquiryTestId}-closed-at`}
              disabledDate={(day) => closedAtProblem(day, submittedAt) !== null}
              format={DATE_FORMAT}
              placeholder={t(
                incidentReportsConstants.CASE_V2_ACTIONS_DUE_DATE_PLACEHOLDER
              )}
            />
          </StepField>
        </StepReveal>
      )}
    </StepRevealGroup>
  )
}

export default SafeguardingEnquiryFields
